import React, { PropsWithChildren } from 'react';

import { usePage } from 'ha/modules/Page/contexts/PageContext';
import { track } from '@ha/analytics';

type ContexValues = { [K: string]: any };

const Context = React.createContext<ContexValues>({});

export const TrackContext: React.FC<ContexValues> = ({
  children,
  ...props
}) => <Context.Provider value={props}>{children}</Context.Provider>;

type Props = {
  name: string;
  getDynamicData?: () => object;
  [K: string]: any;
};

const SmartTrackInternal: React.FC<PropsWithChildren<Props>> = ({
  children,
  name,
  getDynamicData,
  ...dynamicProps
}) => {
  const contextValues = React.useContext(Context);
  const { name: pageName } = usePage();
  const ref = React.useRef<HTMLElement[]>(
    new Array(React.Children.count(children)),
  );

  const trackEvent = React.useCallback(() => {
    const dynamicData = getDynamicData?.();
    const eventName = name;

    const eventAttributes = {
      page: pageName,
      ...contextValues,
      ...dynamicProps,
      ...dynamicData,
    };

    track(eventName, eventAttributes);
  }, [contextValues, dynamicProps, getDynamicData, name, pageName]);

  React.useEffect(() => {
    if (ref.current.length > 0) {
      const childElements = ref.current;

      childElements.forEach(child => {
        const element = child;
        element?.addEventListener?.('click', trackEvent);
      });

      return () => {
        childElements.forEach(child => {
          const element = child;
          element?.removeEventListener?.('click', trackEvent);
        });
      };
    }
    return undefined;
  }, [trackEvent]);

  const childrenWithRef = React.Children.map(children, (child, index) => {
    if (React.isValidElement(child)) {
      const childWithRef = React.cloneElement(child, {
        ref: (el: HTMLElement) => {
          ref.current[index] = el;
        },
      });

      return childWithRef;
    }

    return null;
  });

  return <React.Fragment>{childrenWithRef}</React.Fragment>;
};

const SmartTrack = React.memo(SmartTrackInternal);
export { SmartTrack };
